import React, { useState, useEffect, useRef } from 'react';
import {
  LogOut, RefreshCw, Phone, Mail, Clock,
  CheckCircle, AlertCircle, User, Wallet, Plus, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_PRICES = {
  'ac-repair': 35,
  'ac-installation': 75,
  'heating-repair': 35,
  'heating-installation': 75,
  'duct-cleaning': 30,
  'maintenance': 25,
  'emergency': 85,
};

const getLeadPrice = (serviceType) => LEAD_PRICES[serviceType] || LEAD_PRICES['ac-repair'];

const POLL_INTERVAL_MS = 30000;
const POLL_MAX_ATTEMPTS = 5;
const POLL_RETRY_DELAY_MS = 2000;
const LOW_BALANCE_THRESHOLD = 50;

const SERVICE_LABELS = {
  'ac-repair': 'AC Repair',
  'ac-installation': 'AC Installation',
  'heating-repair': 'Heating Repair',
  'heating-installation': 'Heating Installation',
  'duct-cleaning': 'Duct Cleaning',
  'maintenance': 'Maintenance',
  'emergency': 'Emergency'
};

const PACKAGES = [
  { id: 'starter', amount: 100, label: '$100' },
  { id: 'standard', amount: 250, label: '$250' },
  { id: 'premium', amount: 500, label: '$500' },
  { id: 'enterprise', amount: 1000, label: '$1,000' },
];

const getServiceLabel = (service) => SERVICE_LABELS[service] || service;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const getTimeSince = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000 / 60);
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

/* ── Stats Cards ── */
const StatsCards = ({ availableLeads, claimedLeads, contractor }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
    <div className="bg-white p-6 rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <p className="text-sm text-slate-500">Available Leads</p>
          <p className="text-2xl font-bold text-[#0F172A]">{availableLeads}</p>
        </div>
      </div>
    </div>
    <div className="bg-white p-6 rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <p className="text-sm text-slate-500">Claimed Leads</p>
          <p className="text-2xl font-bold text-[#0F172A]">{claimedLeads}</p>
        </div>
      </div>
    </div>
    <div className="bg-white p-6 rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center">
          <Wallet className="w-6 h-6 text-[#0EA5E9]" />
        </div>
        <div>
          <p className="text-sm text-slate-500">Balance</p>
          <p className="text-2xl font-bold text-[#0EA5E9]">${(contractor.balance || 0).toFixed(2)}</p>
        </div>
      </div>
    </div>
    <div className="bg-white p-6 rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
          <DollarSign className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <p className="text-sm text-slate-500">Total Spent</p>
          <p className="text-2xl font-bold text-[#F97316]">${(contractor.total_spent || 0).toFixed(2)}</p>
        </div>
      </div>
    </div>
  </div>
);

/* ── Add Funds Dialog ── */
const AddFundsDialog = ({ open, onOpenChange, selectedPackage, setSelectedPackage, onAddFunds, isProcessing }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add Funds to Your Account</DialogTitle>
        <DialogDescription>
          Select a package to add funds to your balance. Funds are used when you claim leads.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          {PACKAGES.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg.id)}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedPackage === pkg.id
                  ? 'border-[#F97316] bg-orange-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <p className="text-2xl font-bold text-[#0F172A]">{pkg.label}</p>
              <p className="text-xs text-slate-500 mt-1">~{Math.floor(pkg.amount / 35)} leads</p>
            </button>
          ))}
        </div>
        <Button onClick={onAddFunds} disabled={isProcessing} className="w-full bg-[#F97316] hover:bg-[#EA580C] h-12">
          {isProcessing ? 'Processing...' : `Pay ${PACKAGES.find(p => p.id === selectedPackage)?.label}`}
        </Button>
        <p className="text-xs text-center text-slate-500">Secure payment powered by Stripe</p>
      </div>
    </DialogContent>
  </Dialog>
);

/* ── Lead Card (Available) ── */
const AvailableLeadCard = ({ lead, claimingId, onClaim }) => (
  <div
    key={lead.id}
    data-testid={`available-lead-${lead.id}`}
    className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#0EA5E9]/50 transition-colors"
  >
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <p className="font-semibold text-[#0F172A]">{lead.name}</p>
            <p className="text-xs text-slate-500">{getTimeSince(lead.created_at)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/20">
            {getServiceLabel(lead.service_type)}
          </Badge>
          <Badge variant="outline" className="text-slate-500">
            <Clock className="w-3 h-3 mr-1" />
            {formatDate(lead.created_at)}
          </Badge>
        </div>
        {lead.message && (
          <p className="text-sm text-slate-600 mt-3 bg-slate-50 p-3 rounded-lg">"{lead.message}"</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="text-right">
          <p className="text-2xl font-bold text-[#F97316]">${getLeadPrice(lead.service_type)}</p>
          <p className="text-xs text-slate-400">to claim</p>
        </div>
        <Button
          onClick={() => onClaim(lead.id)}
          disabled={claimingId === lead.id}
          data-testid={`claim-btn-${lead.id}`}
          className="bg-[#F97316] hover:bg-[#EA580C] text-white"
        >
          {claimingId === lead.id ? 'Claiming...' : 'Claim This Lead'}
        </Button>
      </div>
    </div>
  </div>
);

/* ── Lead Card (Claimed) ── */
const ClaimedLeadCard = ({ lead }) => (
  <div
    key={lead.id}
    data-testid={`claimed-lead-${lead.id}`}
    className="bg-white p-6 rounded-xl border border-green-200 bg-green-50/30"
  >
    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-[#0F172A]">{lead.name}</p>
            <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
              Claimed {getTimeSince(lead.claimed_at)}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/20">
            {getServiceLabel(lead.service_type)}
          </Badge>
          <Badge variant="outline" className="text-slate-500">
            ${lead.lead_cost?.toFixed(2) || getLeadPrice(lead.service_type)}
          </Badge>
        </div>
        {lead.message && (
          <p className="text-sm text-slate-600 mt-3 bg-white p-3 rounded-lg border border-slate-100">"{lead.message}"</p>
        )}
      </div>
      <div className="bg-white p-4 rounded-xl border border-slate-200 min-w-[240px]">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-3 font-medium">Contact Information</p>
        <div className="space-y-2">
          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-[#0F172A] hover:text-[#0EA5E9] transition-colors">
            <Phone className="w-4 h-4 text-[#0EA5E9]" />
            <span className="font-medium">{lead.phone}</span>
          </a>
          <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-[#0F172A] hover:text-[#0EA5E9] transition-colors">
            <Mail className="w-4 h-4 text-[#0EA5E9]" />
            <span className="text-sm">{lead.email}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
);

/* ── Main Dashboard Component ── */
const ContractorDashboard = ({ contractor: initialContractor, onLogout }) => {
  const [contractor, setContractor] = useState(initialContractor);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [claimedLeads, setClaimedLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const contractorId = contractor.id;
  const contractorRef = useRef(contractor);
  contractorRef.current = contractor;

  // Stable fetch functions via ref pattern (avoids stale closure / dep array issues)
  const fetchLeadsRef = useRef(null);
  fetchLeadsRef.current = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/contractors/${contractorId}/leads`);
      setAvailableLeads(response.data.available);
      setClaimedLeads(response.data.claimed);
      setContractor(prev => ({ ...prev, balance: response.data.balance }));
    } catch {
      toast.error('Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContractorRef = useRef(null);
  fetchContractorRef.current = async () => {
    try {
      const response = await axios.get(`${API}/contractors/${contractorId}`);
      setContractor(prev => ({ ...prev, ...response.data }));
      localStorage.setItem('contractor', JSON.stringify({ ...contractorRef.current, ...response.data }));
    } catch {
      toast.error('Failed to refresh contractor data');
    }
  };

  const pollPaymentStatusRef = useRef(null);
  pollPaymentStatusRef.current = async (sessionId, attempts = 0) => {
    if (attempts >= POLL_MAX_ATTEMPTS) {
      toast.error('Could not verify payment. Please refresh the page.');
      return;
    }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.status === 'paid') {
        toast.success(response.data.message || 'Funds added successfully!');
        fetchContractorRef.current();
        fetchLeadsRef.current();
        return;
      }
      setTimeout(() => pollPaymentStatusRef.current(sessionId, attempts + 1), POLL_RETRY_DELAY_MS);
    } catch {
      toast.error('Error verifying payment status');
    }
  };

  // Check for payment return on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId) {
      pollPaymentStatusRef.current(sessionId);
      window.history.replaceState({}, '', '/contractors/portal');
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled');
      window.history.replaceState({}, '', '/contractors/portal');
    }
  }, []);

  // Fetch leads on mount + polling interval
  useEffect(() => {
    fetchLeadsRef.current();
    const interval = setInterval(() => fetchLeadsRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const claimLead = async (leadId) => {
    const lead = availableLeads.find(l => l.id === leadId);
    const price = getLeadPrice(lead?.service_type);

    if (contractor.balance < price) {
      toast.error(`Insufficient balance. You need $${price} but have $${contractor.balance?.toFixed(2) || '0.00'}`);
      setShowAddFunds(true);
      return;
    }

    setClaimingId(leadId);
    try {
      const response = await axios.post(`${API}/contractors/${contractorId}/claim/${leadId}`);
      if (response.data.success) {
        toast.success(response.data.message);
        setContractor(prev => ({ ...prev, balance: response.data.new_balance }));
        localStorage.setItem('contractor', JSON.stringify({ ...contractor, balance: response.data.new_balance }));
        fetchLeadsRef.current();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim lead');
      if (error.response?.data?.detail?.includes('Insufficient')) {
        setShowAddFunds(true);
      }
    } finally {
      setClaimingId(null);
    }
  };

  const handleAddFunds = async () => {
    setIsProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/add-funds`, {
        contractor_id: contractorId,
        package: selectedPackage,
        origin_url: window.location.origin
      });
      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch {
      toast.error('Failed to initiate payment');
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#0F172A] rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">N</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {contractor.company_name}
                </h1>
                <p className="text-xs text-slate-500">Contractor Dashboard</p>
              </div>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-slate-100 rounded-xl px-4 py-2 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-[#0EA5E9]" />
              <span className="font-bold text-[#0F172A]">${(contractor.balance || 0).toFixed(2)}</span>
              <Button size="sm" className="ml-2 bg-[#F97316] hover:bg-[#EA580C] h-7 px-2" onClick={() => setShowAddFunds(true)}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
            </div>
            <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <StatsCards
          availableLeads={availableLeads.length}
          claimedLeads={claimedLeads.length}
          contractor={contractor}
        />

        {/* Low Balance Warning */}
        {(contractor.balance || 0) < LOW_BALANCE_THRESHOLD && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">
                <strong>Low Balance:</strong> Add funds to continue claiming leads.
              </p>
            </div>
            <Button onClick={() => setShowAddFunds(true)} size="sm" className="bg-yellow-600 hover:bg-yellow-700">
              Add Funds
            </Button>
          </div>
        )}

        {/* Available Leads */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Available Leads
            </h2>
            <Button variant="outline" onClick={() => fetchLeadsRef.current()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {availableLeads.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No new leads available right now</p>
              <p className="text-sm text-slate-400 mt-2">Check back soon or refresh to see new leads</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {availableLeads.map((lead) => (
                <AvailableLeadCard key={lead.id} lead={lead} claimingId={claimingId} onClaim={claimLead} />
              ))}
            </div>
          )}
        </div>

        {/* Claimed Leads */}
        <div>
          <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Claimed Leads
          </h2>
          {claimedLeads.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No claimed leads yet</p>
              <p className="text-sm text-slate-400 mt-2">Claim leads above to see contact information</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {claimedLeads.map((lead) => (
                <ClaimedLeadCard key={lead.id} lead={lead} />
              ))}
            </div>
          )}
        </div>
      </main>

      <AddFundsDialog
        open={showAddFunds}
        onOpenChange={setShowAddFunds}
        selectedPackage={selectedPackage}
        setSelectedPackage={setSelectedPackage}
        onAddFunds={handleAddFunds}
        isProcessing={isProcessingPayment}
      />
    </div>
  );
};

export default ContractorDashboard;
